import { getFontFamilyWithFallbacks, parseFontStr, rgbToHex } from '@wix/thunderbolt-becky-root'
import { color as colorFunctions, createFontValue, getTextPresets } from '@wix/thunderbolt-commons'
import { StyleProperties } from '@wix/thunderbolt-becky-types'

const PRIME_COLORS_REFERENCES = ['white/black', 'black/white', 'primery-1', 'primery-2', 'primery-3']
const NON_PRIME_COLORS_PREFIX = 'color-'

/* carmi equivalent is in tpaStyleUtils.carmi.ts */
export const getSiteColors = (themeColors: Array<string>) => {
	return themeColors.reduce((acc, color, index) => {
		if ((index >= 1 && index <= 5) || (index >= 11 && index <= 65)) {
			acc.push({
				name: `color_${index}`,
				value: rgbToHex(color),
				reference: index <= 5 ? PRIME_COLORS_REFERENCES[index - 1] : `${NON_PRIME_COLORS_PREFIX}${index - 10}`,
			})
		}
		return acc
	}, [] as SiteColors)
}

type OldColorObject = {
	value: {
		cssColor?: string
		color?: { value: string }
		rgba?: string
	}
}

type SiteColors = Array<{ name: string; value: string; reference: string }>

/* carmi equivalent is in tpaStyleUtils.carmi.ts */
export const getColorValue = (
	colorStringOrObject: string | OldColorObject,
	alpha: string,
	themeColors: Array<string>
) => {
	if (typeof colorStringOrObject === 'object') {
		const {
			value: { cssColor, color, rgba },
		} = colorStringOrObject as OldColorObject
		return {
			themeName: undefined,
			value: cssColor || color?.value || rgba,
		}
	} else {
		// color is string
		const parsedThemeColorName = colorStringOrObject.match(/^color_(.*)/)
		if (parsedThemeColorName) {
			// theme color
			const colValHex = themeColors[Number(parsedThemeColorName[1])]
			return {
				themeName: colorStringOrObject,
				value: alpha ? colorFunctions.formatColor(colValHex, alpha) : colValHex,
			}
		} else {
			return {
				themeName: undefined,
				value: colorStringOrObject,
			}
		}
	}
}

type StyleParamByType = { [type: string]: Record<string, string> }

/* unlike the carmi impl in tpaStyleUtils.carmi.ts,
 * this function returns the params grouped by type (color, number, etc.)
 */
export const splitPropertiesValues = (styleProperties: StyleProperties): StyleParamByType => {
	return Object.entries(styleProperties).reduce((acc, [rawKey, value]) => {
		if (value) {
			const split: Array<string> | null = rawKey.match('^param_([^_]+)_(.*)') // 'param_type_name' => (1: type, 2: name)
			if (split) {
				const [, type, name] = split
				acc[type] = acc[type] || {}
				acc[type][name] = value as string
			}
		}
		return acc
	}, {} as StyleParamByType)
}

type FontJSONFromStyleString = {
	family: string
	style: {
		bold: boolean
		italic: boolean
		underline: boolean
	}
	size: number
	preset: string
	editorKey: string
	fontStyleParam: boolean
	fontParam: boolean
	cssFontFamily: string
	value: string
	theme: string
}

type FontJSON = {
	family: string
	style: {
		bold: boolean
		italic: boolean
		underline: boolean
	}
	size: string | number
	preset: string
	editorKey: string
	fontStyleParam: boolean
	fontParam: boolean
	cssFontFamily: string
	value: string
	theme: string
}

const parseJSON = (val: string) => {
	if (!val) {
		return val
	}

	try {
		return JSON.parse(val)
	} catch (e) {
		if (process.env.NODE_ENV === 'development') {
			console.warn('parseJSON failed parsing value:', val)
		}
		return null
	}
}

const doubleQuotesEscapeForIE = (str: string): string => str.replace(/''/g, "'")

const updateCustomSavedFontValue = (fontJson: FontJSONFromStyleString): FontJSON => {
	const family = fontJson.family
	const fontFamilyWithFallbacks = getFontFamilyWithFallbacks(family)
	const fontSize = fontJson.size
	const size = `${fontSize}px`
	const lineHeight = `${Math.floor(fontSize * 1.25)}px`
	const style = fontJson.style
	const italic = style?.italic ? 'italic' : 'normal'
	const weight = style?.bold ? 'bold' : 'normal'
	const variant = 'normal'
	const underline = style?.underline ? 'text-decoration:underline;' : ''
	const value = createFontValue(italic, variant, weight, size, lineHeight, family, underline)
	return { ...fontJson, value, family: fontFamilyWithFallbacks }
}

const updatePresetSelectedFromNewUILib = (fontJson: FontJSONFromStyleString): FontJSON => {
	const themeFont = parseFontStr(fontJson.preset)
	const size = `${themeFont.size}px`
	const variant = themeFont.variant
	const family = themeFont.family[0].toLowerCase()
	const lineHeight = themeFont.lineHeight
	const bold = themeFont.weight
	const italic = themeFont.style

	return {
		...fontJson,
		size,
		style: { bold: bold === 'bold', italic: italic === 'italic', underline: false },
		value: createFontValue(italic, variant, bold, size, lineHeight, family, ''),
	}
}

const updateSavedFontValueFromNewUILib = (fontJson: FontJSONFromStyleString): FontJSON => {
	const fontWithFallbacks = getFontFamilyWithFallbacks(fontJson.family)
	const italic = fontJson.style?.italic ? 'italic' : 'normal'
	const weight = fontJson.style?.bold ? 'bold' : 'normal'
	const underline = fontJson.style?.underline ? ';text-decoration:underline;' : ';'
	const value = `font-family:${fontWithFallbacks};font-styles:${italic};font-weight:${weight}${underline}`
	return { ...fontJson, value }
}

const updateSavedFontValue = (fontJson: FontJSONFromStyleString): FontJSON => {
	const fontFamily = fontJson.cssFontFamily || fontJson.value
	return {
		...fontJson,
		size: 0,
		style: { bold: false, italic: false, underline: false },
		family: fontJson.value,
		value: doubleQuotesEscapeForIE(`font-family:${fontFamily}`),
	}
}

const updatePresetSavedFontValue = (fontJson: FontJSONFromStyleString, themeFonts: Array<string>): FontJSON => {
	const themeFont = getTextPresets(themeFonts)[fontJson.preset]
	const size = parseInt(themeFont.size, 10)
	const family = themeFont.fontFamily
	const bold = themeFont.weight
	const italic = themeFont.style
	const value = themeFont.value

	return {
		...fontJson,
		size,
		family,
		style: { bold: bold === 'bold', italic: italic === 'italic', underline: false },
		value,
	}
}

export const getTpaFontParamValue = (fontStyleParamValue: string, themeFonts: Array<string>): FontJSON | null => {
	const parsedJson = parseJSON(fontStyleParamValue)

	if (!parsedJson) {
		return null
	}

	if (parsedJson?.fontStyleParam) {
		if (parsedJson?.preset === 'Custom') {
			return updateCustomSavedFontValue(parsedJson)
		}

		if (parsedJson?.theme) {
			return updatePresetSelectedFromNewUILib(parsedJson)
		}

		return updatePresetSavedFontValue(parsedJson, themeFonts)
	}

	if (parsedJson?.fontParam) {
		if (parsedJson?.preset === 'None') {
			return updateSavedFontValueFromNewUILib(parsedJson)
		}

		return updateSavedFontValue(parsedJson)
	}

	return parsedJson
}
